
import {
  defineComponent,
  ref,
  Ref,
  computed,
  watch,
  PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import SelectButton from 'primevue/selectbutton';
import ProgressSpinner from 'primevue/progressspinner';

export default defineComponent({
  name: 'ImageCompressionModal',

  components: {
    Dialog,
    Button,
    SelectButton,
    ProgressSpinner,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },
    file: {
      type: Object as PropType<globalThis.File | null>,
      required: false,
      default: null,
    },
    context: {
      type: String as PropType<'email' | 'display' | 'file-manager'>,
      required: false,
      default: 'file-manager',
    },
  },

  emits: {
    'on-close-dialog': null,
    'on-import': (file: globalThis.File, isCompressed: boolean) => true,
  },

  setup(props, context) {
    const { t } = useI18n();

    const displayModal = computed(() => props.displayDialog);
    const selectedVersion = ref('compressed');
    const versionOptions = ref([
      { label: 'Original', value: 'original' },
      { label: 'Compressé', value: 'compressed' },
    ]);

    const originalPreview: Ref<string | null> = ref(null);
    const compressedPreview: Ref<string | null> = ref(null);
    const originalSize = ref(0);
    const compressedSize = ref(0);
    const originalDimensions = ref({ width: 0, height: 0 });
    const compressedDimensions = ref({ width: 0, height: 0 });
    const compressing = ref(false);
    const compressedFile: Ref<globalThis.File | null> = ref(null);

    // Zoom functionality
    const showZoom = ref(false);
    const zoomedImage: Ref<string | null> = ref(null);

    const compressionRatio = computed(() => {
      if (originalSize.value === 0) return 0;
      const ratio = ((originalSize.value - compressedSize.value) / originalSize.value) * 100;
      return Math.round(ratio);
    });

    const canImport = computed(() => {
      if (compressing.value) return false;
      if (selectedVersion.value === 'compressed') {
        return compressedFile.value !== null;
      }
      return props.file !== null;
    });

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const loadImageDimensions = (file: globalThis.File): Promise<{ width: number; height: number }> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    };

    const compressImage = async (file: globalThis.File): Promise<globalThis.File> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const img = new Image();
          img.onload = () => {
            let { width, height } = img;
            let quality = 0.85;

            if (props.context === 'email') {
              // Email : max 800px de large (standard email)
              const maxWidth = 900;

              // Ne réduire que si l'image est plus large que 800px
              if (width > maxWidth) {
                const ratio = maxWidth / width;
                width = maxWidth;
                height = Math.round(height * ratio);
              }

              // Qualité selon taille du fichier
              if (file.size > 2 * 1024 * 1024) { // > 2MB
                quality = 0.7;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                quality = 0.75;
              } else if (file.size > 500 * 1024) { // > 500KB
                quality = 0.8;
              } else {
                quality = 0.85;
              }
            } else if (props.context === 'display') {
              // Display : privilégier la qualité avec ratio de réduction
              let reductionRatio = 1.0;

              if (file.size > 2 * 1024 * 1024) { // > 2MB
                reductionRatio = 0.6;
                quality = 0.75;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                reductionRatio = 0.8;
                quality = 0.85;
              } else {
                reductionRatio = 1.0;
                quality = 0.9;
              }

              if (reductionRatio < 1.0) {
                width = Math.round(width * reductionRatio);
                height = Math.round(height * reductionRatio);
              }
            } else {
              // File Manager : modéré avec ratio de réduction
              let reductionRatio = 1.0;

              if (file.size > 2 * 1024 * 1024) { // > 2MB
                reductionRatio = 0.5;
                quality = 0.75;
              } else if (file.size > 1 * 1024 * 1024) { // > 1MB
                reductionRatio = 0.6;
                quality = 0.8;
              } else if (file.size > 500 * 1024) { // > 500KB
                reductionRatio = 0.8;
                quality = 0.85;
              } else {
                reductionRatio = 1.0;
                quality = 0.9;
              }

              if (reductionRatio < 1.0) {
                width = Math.round(width * reductionRatio);
                height = Math.round(height * reductionRatio);
              }
            }

            // Create canvas and compress
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Canvas context not available'));
              return;
            }

            ctx.drawImage(img, 0, 0, width, height);

            // Convert to blob with adaptive quality
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Compression failed'));
                  return;
                }
                const compressedFile = new File([blob], file.name, {
                  type: file.type,
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              },
              file.type,
              quality
            );
          };
          img.onerror = reject;
          img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    };

    const processImage = async (file: globalThis.File) => {
      if (!file) return;

      // Load original
      originalSize.value = file.size;
      originalPreview.value = URL.createObjectURL(file);
      originalDimensions.value = await loadImageDimensions(file);

      // Compress
      compressing.value = true;
      try {
        const compressed = await compressImage(file);

        // Vérifier si la compression a réduit la taille
        if (compressed.size < file.size) {
          // Compression réussie
          compressedFile.value = compressed;
          compressedSize.value = compressed.size;
          compressedPreview.value = URL.createObjectURL(compressed);
          compressedDimensions.value = await loadImageDimensions(compressed);
        } else {
          // La compression a augmenté la taille, utiliser l'original
          compressedFile.value = file;
          compressedSize.value = file.size;
          compressedPreview.value = URL.createObjectURL(file);
          compressedDimensions.value = originalDimensions.value;
        }
      } catch (error) {
        console.error('Compression error:', error);
        // En cas d'erreur, utiliser l'original
        compressedFile.value = file;
        compressedSize.value = file.size;
        compressedPreview.value = URL.createObjectURL(file);
        compressedDimensions.value = originalDimensions.value;
      } finally {
        compressing.value = false;
      }
    };

    const zoomImage = (imageUrl: string | null) => {
      if (imageUrl) {
        zoomedImage.value = imageUrl;
        showZoom.value = true;
      }
    };

    const handleClose = () => {
      // Cleanup URLs
      if (originalPreview.value) URL.revokeObjectURL(originalPreview.value);
      if (compressedPreview.value) URL.revokeObjectURL(compressedPreview.value);

      // Reset state
      originalPreview.value = null;
      compressedPreview.value = null;
      originalSize.value = 0;
      compressedSize.value = 0;
      originalDimensions.value = { width: 0, height: 0 };
      compressedDimensions.value = { width: 0, height: 0 };
      selectedVersion.value = 'compressed';
      compressedFile.value = null;

      context.emit('on-close-dialog');
    };

    const handleImport = () => {
      const fileToImport = selectedVersion.value === 'compressed' ? compressedFile.value : props.file;

      if (!fileToImport) {
        return;
      }

      context.emit('on-import', fileToImport, selectedVersion.value === 'compressed');
    };

    watch(
      () => props.file,
      (newFile) => {
        if (newFile && props.displayDialog) {
          processImage(newFile);
        }
      },
      { immediate: true }
    );

    return {
      t,
      displayModal,
      selectedVersion,
      versionOptions,
      originalPreview,
      compressedPreview,
      originalSize,
      compressedSize,
      originalDimensions,
      compressedDimensions,
      compressionRatio,
      compressing,
      canImport,
      formatFileSize,
      showZoom,
      zoomedImage,
      zoomImage,
      handleClose,
      handleImport,
    };
  },
});
